/* ============================================================================
 *  Split acceptance projection
 * ----------------------------------------------------------------------------
 *  Faithful client-side mirror of core-platform
 *  `Modules/Billing/Actions/Split/AddSplitCoproducerEntryAction`. Lets the owner
 *  preview what each active receiver's percentile WILL become once the pending
 *  invitation(s) are accepted, WITHOUT a round-trip.
 *
 *  Rules (must match the backend exactly):
 *   - The invite's percentile is an ABSOLUTE (integer) share of the TOTAL
 *     (25 → 25% of 100).
 *   - Mentoring entries AND the other coproducers keep their shares FIXED.
 *   - Only the OWNER's residual entry absorbs the new receiver:
 *       ownerShare = 100 - sum(mentoring) - sum(other coproducers) - share
 *   - Each open invitation is applied in turn (acceptance is one-at-a-time on the
 *     backend), so the projection assumes ALL currently-open invites are accepted.
 *   - The owner is identified by email (the split editor is the community owner).
 * ========================================================================== */

const MENTORING = 'mentoring';
const OPEN_STATUSES = new Set(['pending', 'awaiting_wallet']);

function round2(n) {
    return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

function emailOf(receiver) {
    const raw = receiver?.email?.address ?? receiver?.email ?? '';
    return typeof raw === 'string' ? raw.trim().toLowerCase() : '';
}

/**
 * @param {Array}  receivers   Active config.customer entries ({ id, percentile, type, email }).
 * @param {Array}  invitations Pending split invitations ({ id, percentile, status, invited_user }).
 * @param {string} ownerEmail  The community owner's email (= the current editor) — used
 *                             to find the residual entry that absorbs new receivers.
 * @returns {{ receivers: Object<string,number>, invitations: Object<string,number> }}
 *          Projected percentiles keyed by receiver id / invitation id — only entries
 *          whose value actually changes are included.
 */
export function projectSplitAcceptance(receivers = [], invitations = [], ownerEmail = '') {
    const openInvites = (invitations || []).filter((i) => OPEN_STATUSES.has(i?.status));
    if (openInvites.length === 0) return { receivers: {}, invitations: {} };

    const ownerEmailLc = (ownerEmail || '').trim().toLowerCase();

    // Working set from the current receivers.
    let entries = (receivers || []).map((r) => ({
        userId: r.id,
        percentile: Number(r.percentile || 0),
        type: r.type === MENTORING ? MENTORING : 'coproduction',
        isOwner: r.type !== MENTORING && !!ownerEmailLc && emailOf(r) === ownerEmailLc,
        inviteId: null,
    }));

    for (const inv of openInvites) {
        const invitedId = inv?.invited_user?.id ?? inv?.user_id ?? null;
        const share = round2(Number(inv?.percentile || 0));

        // Idempotent: drop any prior coproduction entry for the invited user.
        if (invitedId != null) {
            entries = entries.filter((e) => !(e.type !== MENTORING && e.userId === invitedId));
        }

        const mentoring = entries.filter((e) => e.type === MENTORING);
        const coproducers = entries.filter((e) => e.type !== MENTORING);
        const ownerEntry = coproducers.find((e) => e.isOwner) || null;
        const otherCoproducers = coproducers.filter((e) => !e.isOwner);

        // Without a resolvable owner residual we can't faithfully project — skip.
        if (!ownerEntry) continue;

        const mentoringTotal = mentoring.reduce((s, e) => s + e.percentile, 0);
        const otherTotal = otherCoproducers.reduce((s, e) => s + e.percentile, 0);

        let ownerShare = round2(100 - mentoringTotal - otherTotal - share);
        let finalShare = share;
        if (ownerShare < 0) {
            // Pool shrank: cap the receiver, owner floors at 0 (mirrors the backend).
            finalShare = Math.max(0, round2(100 - mentoringTotal - otherTotal));
            ownerShare = 0;
        }
        ownerEntry.percentile = ownerShare;

        entries = [
            ...mentoring,
            ...otherCoproducers,
            ownerEntry,
            {
                userId: invitedId,
                percentile: finalShare,
                type: 'coproduction',
                isOwner: false,
                inviteId: inv.id,
            },
        ];
    }

    // Projections from the FINAL state — only the owner's residual actually changes.
    const receiverProjection = {};
    for (const r of receivers || []) {
        if (r.type === MENTORING) continue;
        const match = entries.find((e) => e.inviteId == null && e.userId === r.id);
        if (!match) continue;
        if (round2(match.percentile) !== round2(Number(r.percentile || 0))) {
            receiverProjection[r.id] = round2(match.percentile);
        }
    }

    const invitationProjection = {};
    for (const inv of openInvites) {
        const match = entries.find((e) => e.inviteId === inv.id);
        if (match) invitationProjection[inv.id] = round2(match.percentile);
    }

    return { receivers: receiverProjection, invitations: invitationProjection };
}

/** Format a percentile for display in pt-BR (comma decimal, no trailing zero). */
export function formatProjectedPct(n) {
    const num = round2(n);
    if (Number.isInteger(num)) return String(num);
    return num.toFixed(2).replace(/0$/, '').replace('.', ',');
}
